import {useCallback, useEffect, useState} from 'react';
import {Button, Modal, Spin, Typography} from 'antd';
import {useHistory, useParams} from 'react-router-dom';
import {SchoolService} from './SchoolService';
import {StudentsService} from '../Students';
import {EnrollmentsService} from '../Enrollments/EnrollmentsService';

const schoolsService = new SchoolService();
const studentsService = new StudentsService();
const enrollmentsService = new EnrollmentsService();

const getErrorMessage = (error: any, fallback: string) => {
  const graphQLErrorMessage =
    Array.isArray(error?.errors) && typeof error.errors[0]?.message === 'string'
      ? error.errors[0].message
      : undefined;

  return (
    graphQLErrorMessage ??
    (typeof error?.message === 'string' ? error.message : fallback)
  );
};

const getEnrollmentId = (enrollment: any) => enrollment?._id ?? enrollment?.id;

const getEnrollmentTime = (enrollment: any) =>
  new Date(enrollment?.updatedAt ?? enrollment?.createdAt ?? 0).getTime();

const formatValue = (value: any) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

const findEditableClassEnrollment = (
  enrollments: any[],
  classSchoolId: string,
) => {
  const activeEnrollments = (enrollments ?? []).filter(
    enrollment => enrollment?.isDeleted !== true,
  );
  const classEnrollment = activeEnrollments.find(
    enrollment => enrollment?.classId === classSchoolId,
  );

  if (classEnrollment) {
    return classEnrollment;
  }

  return activeEnrollments.sort(
    (first, second) => getEnrollmentTime(second) - getEnrollmentTime(first),
  )[0];
};

export const ClassSchoolDetails = () => {
  const params: {classSchoolId: string} = useParams();
  const history = useHistory();
  const {Title, Text} = Typography;
  const [classSchool, setClassSchool] = useState<any>();
  const [students, setStudents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRemovingStudent, setIsRemovingStudent] = useState<boolean>(false);
  const [isEditingClassEnrollment, setIsEditingClassEnrollment] =
    useState<boolean>(false);
  const classSchoolId =
    classSchool?.id ?? classSchool?._id ?? params.classSchoolId;
  const schoolId =
    classSchool?.schoolId ??
    classSchool?.school?.id ??
    classSchool?.school?._id;
  const schoolName = classSchool?.school?.name ?? classSchool?.schoolName;

  const studentColumns = [
    {
      title: 'Name',
      width: 180,
      render: (student: any) => formatValue(student.name),
    },
    {
      title: 'Student ID',
      width: 240,
      render: (student: any) => formatValue(student.id ?? student._id),
    },
    {
      title: 'User ID',
      width: 240,
      render: (student: any) => formatValue(student.userId ?? student.user?.id),
    },
    {
      title: 'Gender',
      width: 110,
      render: (student: any) => formatValue(student.gender),
    },
    {
      title: 'Age',
      width: 90,
      render: (student: any) => formatValue(student.age),
    },
    {
      title: 'Birth Date',
      width: 150,
      render: (student: any) =>
        formatValue(student.birthDate ?? student.birthdate),
    },
  ];

  const loadClassSchool = useCallback(async () => {
    setIsLoading(true);
    try {
      const [classSchoolResult, studentsResult] = await Promise.all([
        schoolsService.getClassSchoolById(params.classSchoolId),
        studentsService.getStudentsByClassId(params.classSchoolId),
      ]);

      setClassSchool(classSchoolResult);
      setStudents(studentsResult ?? []);
    } catch (error: any) {
      alert(
        `Error loading class: ${getErrorMessage(
          error,
          'Failed to load class details.',
        )}`,
      );
    } finally {
      setIsLoading(false);
    }
  }, [params.classSchoolId]);

  const handleRemoveStudentFromClass = useCallback(
    async (student: any) => {
      Modal.confirm({
        title: 'Remove student from class?',
        content:
          'This will remove the school and class references from the student and create a free enrollment.',
        okText: 'Remove',
        okType: 'danger',
        onOk: async () => {
          setIsRemovingStudent(true);
          try {
            await studentsService.removeStudentFromClass(student);
            await loadClassSchool();
            alert('Student has been removed from the class.');
          } catch (error: any) {
            alert(
              `Error removing student: ${getErrorMessage(
                error,
                'Failed to remove student from class.',
              )}`,
            );
          } finally {
            setIsRemovingStudent(false);
          }
        },
      });
    },
    [loadClassSchool],
  );

  const handleAddEnrollment = useCallback(() => {
    history.push('/enrollments/create', {
      classSchoolId: params.classSchoolId,
      classSchoolName: classSchool?.name,
    });
  }, [classSchool?.name, history, params.classSchoolId]);

  const handleEditEnrollment = useCallback(async () => {
    setIsEditingClassEnrollment(true);

    try {
      const enrollmentsResult =
        await enrollmentsService.getClassSchoolEnrollments(
          1,
          100,
          true,
          params.classSchoolId,
        );
      const editableEnrollment = findEditableClassEnrollment(
        enrollmentsResult.items,
        params.classSchoolId,
      );
      const enrollmentId = getEnrollmentId(editableEnrollment);

      if (!enrollmentId) {
        alert('No enrollment found for this class.');
        return;
      }

      history.push(`/enrollments/${enrollmentId}/edit`);
    } catch (error: any) {
      alert(
        `Error loading enrollment: ${getErrorMessage(
          error,
          'Failed to load class enrollments.',
        )}`,
      );
    } finally {
      setIsEditingClassEnrollment(false);
    }
  }, [history, params.classSchoolId]);

  useEffect(() => {
    loadClassSchool();
  }, [loadClassSchool]);

  return (
    <div style={{padding: 24}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          alignItems: 'flex-start',
          marginBottom: 16,
        }}>
        <div>
          <Title level={2} style={{marginBottom: 4}}>
            {classSchool?.name ?? 'Class'}{' '}
            <Text type="secondary" style={{fontSize: 16}}>
              (Class ID: {formatValue(classSchoolId)})
            </Text>
          </Title>
          <Text type="secondary">
            {formatValue(schoolName)} (School ID: {formatValue(schoolId)})
          </Text>
        </div>
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
          <Button type="primary" onClick={handleAddEnrollment}>
            Add Enrollment
          </Button>
          <Button
            loading={isEditingClassEnrollment}
            onClick={handleEditEnrollment}>
            Edit Enrollment
          </Button>
          <Button onClick={() => history.goBack()}>Back</Button>
        </div>
      </div>

      {isLoading ? (
        <Spin size="large" />
      ) : (
        <div
          style={{
            maxWidth: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
          <table
            style={{
              width: '100%',
              minWidth: 1190,
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}>
            <thead>
              <tr>
                {studentColumns.map(column => (
                  <th
                    key={column.title}
                    style={{
                      border: '1px solid #ddd',
                      padding: '10px',
                      textAlign: 'left',
                      width: column.width,
                    }}>
                    {column.title}
                  </th>
                ))}
                <th
                  style={{
                    border: '1px solid #ddd',
                    padding: '10px',
                    textAlign: 'left',
                    width: 180,
                  }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {students.length ? (
                students.map(student => {
                  const studentId = student.id ?? student._id;

                  return (
                    <tr key={studentId}>
                      {studentColumns.map(column => (
                        <td
                          key={column.title}
                          style={{
                            border: '1px solid #ddd',
                            padding: '10px',
                            wordBreak: 'break-all',
                            fontWeight:
                              column.title === 'Name' ? 'bold' : undefined,
                          }}>
                          {column.render(student)}
                        </td>
                      ))}
                      <td
                        style={{
                          border: '1px solid #ddd',
                          padding: '10px',
                        }}>
                        <div
                          style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() =>
                              history.push(`/students/${studentId}/edit`)
                            }>
                            Update
                          </Button>
                          <Button
                            danger
                            size="small"
                            loading={isRemovingStudent}
                            onClick={() =>
                              handleRemoveStudentFromClass(student)
                            }>
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={studentColumns.length + 1}
                    style={{
                      border: '1px solid #ddd',
                      padding: '16px',
                      textAlign: 'center',
                    }}>
                    No students in this class.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
